import { appConfig } from '@/config/app-config'
import type { ProjectEvent } from '@/modules/projects/types'

import type { AppStore } from './create-store'

/**
 * The activity history is the one thing besides the user id that the browser keeps between visits, until the API has an endpoint
 * for it. Only the newest `historyLimit` entries are kept, so it never grows without bound.
 */
export function readHistory(storage: Storage | null): ProjectEvent[] {
  if (!storage) return []
  try {
    const parsed: unknown = JSON.parse(storage.getItem(appConfig.historyKey) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter((event): event is ProjectEvent => typeof event?.id === 'string' && typeof event.projectId === 'string' && typeof event.createdAt === 'string') : []
  } catch {
    return []
  }
}

/** Writes the history whenever it changes. Returns a function that stops. */
export function persistHistory(store: AppStore, storage: Storage | null): () => void {
  if (!storage) return () => undefined
  let last = store.getState().projects.events
  return store.subscribe(() => {
    const events = store.getState().projects.events
    if (events === last) return
    last = events
    const newest = Object.values(events.entities)
      .filter((event): event is ProjectEvent => Boolean(event))
      .toSorted((a, b) => a.createdAt.localeCompare(b.createdAt))
      .slice(-appConfig.historyLimit)
    try {
      storage.setItem(appConfig.historyKey, JSON.stringify(newest))
    } catch {
      // Storage full or unavailable: the history is still kept for this visit.
    }
  })
}
